import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import { dateToNumber, randomKey } from 'lib/riskbase';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { GlobalFieldInstanceScreen } from 'screens/FieldComponents/GlobalFieldInstanceScreen';
import { ASSET_SAVE_FIELDS, doAssetOpenOverview } from 'store/actions';
import {
  selectAssetFieldResultsForGroup,
  selectAssetOrganisationId,
} from 'store/assets/selectors';
import {
  selectUserOrganisationFeatures,
  selectUserOrganisationUuidToUid,
} from 'store/auth/selectors';
import { selectOrganisationFieldGroup } from 'store/organisations/selectors';
import { PinNew } from 'store/plan/types';
import { AppContextEnum, FieldResult } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSET],
  Screens.ASSET_FIELDSTANDARD
>;

export const FieldStandardInstanceScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const { assetId, groupId } = route.params;

  const organisationId = useSelector((state) =>
    selectAssetOrganisationId(state, assetId),
  );

  const organisationUid = useSelector((state) =>
    selectUserOrganisationUuidToUid(state, organisationId),
  );
  const userFeatures = useSelector((state) =>
    selectUserOrganisationFeatures(state, organisationUid),
  );

  const fieldResults = useSelector((state) =>
    selectAssetFieldResultsForGroup(state, assetId, groupId),
  );

  const group = useSelector((state) =>
    selectOrganisationFieldGroup(state, organisationId, groupId),
  );

  const fieldKeys = React.useRef(
    (group?.fieldIds || []).reduce<Record<string, string>>((acc, fieldId) => {
      acc[fieldId] = `${fieldId}-${randomKey()}`;
      return acc;
    }, {}),
  );

  const handleSave = React.useCallback(
    (results: Record<string, FieldResult>) =>
      dispatch(
        ASSET_SAVE_FIELDS({
          assetId,
          results,
        }),
      ),
    [assetId, dispatch],
  );

  const handleChange = React.useCallback(
    (
      fieldId: string,
      value: string | string[] | null,
      pin?: PinNew | '' | null,
    ) =>
      handleSave({
        [fieldId]: {
          fieldId,
          ts: dateToNumber(),
          value: value || value === '0' ? value : '',
          pin: pin || '',
          action: null,
        },
      }),
    [handleSave],
  );

  React.useEffect(() => {
    if (!group) {
      if (screenSize === ScreenSize.large) {
        dispatch(doAssetOpenOverview({ assetId }));
      } else {
        navigation.goBack();
      }
    }
  }, [group, dispatch, screenSize, assetId, navigation]);

  if (!group) {
    return null;
  }

  return (
    <GlobalFieldInstanceScreen
      context={{
        context: AppContextEnum.localAsset,
        assetId,
      }}
      group={group}
      fields={group.fields}
      editableFieldIds={userFeatures.editassetfields ? group.fieldIds : []}
      requiredFieldIds={[]}
      fieldKeys={fieldKeys.current}
      onChange={handleChange}
      results={fieldResults}
      onPressBackButton={
        screenSize === ScreenSize.small ? navigation.goBack : undefined
      }
      showInvalid={false}
    />
  );
};
